// GENERAL
import { FormProvider, useForm } from "react-hook-form";
import React, { useEffect, Fragment } from "react";
import getFontAwesomeIcon from "../../../../../utils/getFontAwesomeIcon";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
// COMPONENTS
import {
	BaseButton,
	BaseText,
	SupplyFormGuardrail,
} from "../../../../../components";
import SelectedShipmentItemsTable from "../SelectedShipmentItemsTable";
import FulfilledShipLoadingScreen from "../../../../Shipments/view/components/FulfilledShipLoadingScreen";

// TYPES, FIELDS, UTILS
import {
	ShipmentField,
	ShipmentData,
	EditShipmentFormProps,
	ShippingMethodOptions,
	ShippingCarrier,
	ShipmentOrderSelectedItemTypes,
	CarrierAccount,
	ModifiedSerialNumbersOptionsArray,
	ShipmentFieldsConfig,
} from "../../../types";
import dummyUpdateShipmentFields from "../../../viewModel/FIELDS/DUMMYUPDATESHIPMENTFIELDS.json";
import {
	fulfillShipment,
	handleShipmentDownload,
	saveShipment,
} from "../../../api/UpdateShipmentApi";
import {
	checkDimensions,
	formatShipmentData,
	getEditShipmentFormOptions,
	renderEditShipmentFormInput,
	validateFormOnSubmit,
} from "../../../helpers";
import { useToaster } from "../../../../../hooks/useToasterContext";

const sectionClassesMap: { [key: string]: string } = {
	col1: "w-56 min-w-56 max-1893px:w-150px max-1893px:min-w-150px",
	col2: "w-16 min-w-16",
	col3: "w-24 min-w-24",
	col4: "w-150px min-w-150px",
	col5: "grid grid-cols-4 w-80 min-w-80 gap-x-3 max-1893px:w-300px max-1893px:min-w-300px",
	col6: "w-44 min-w-44 max-1893px:w-120px max-1893px:min-w-120px",
	col7: "w-48 min-w-48  max-1893px:w-36 max-1893px:min-w-36",
	col8: "w-56 min-w-56 grid grid-rows-3 items-start h-1/2 gap-x-4 max-1893px:w-56 max-1893px:min-w-56",
};

const EditShipmentForm: React.FC<EditShipmentFormProps> = ({
	formClasses,
	optionsArray,
	shipmentAddress,
	shipmentNumber,
}) => {
	let { addressee, state, line1, line2, city, zip, attention, uuid } =
		shipmentAddress;
	const [isGuardrailOpen, setIsGuardrailOpen] = useState(false);
	const { addToaster } = useToaster();
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const internalId = queryParams.get("internalId");

	const [isFulfillAndShipLoading, setIsFulfillAndShipLoading] =
		useState(false);
	const [isSavingShipmentLoading, setIsSavingShipmentLoading] =
		useState(false);
	const [carrierAccountNumber, setCarrierAccountNumber] = useState<
		CarrierAccount[]
	>([]);
	const [shippingMethodOptions, setShippingMethodOptions] = useState<
		ShippingMethodOptions[] | null
	>(null);

	const shipmentsUrl = `/shipments?internalId=${internalId}`;
	const shipmentsItemsUrl = `/shipment-items?internalId=${internalId}`;
	const navigate = useNavigate();
	const [selectedItemsData, setSelectedItemsData] = useState<
		ShipmentOrderSelectedItemTypes[]
	>([]);

	// Add state to track saved serial numbers
	const [savedSerialNumbers, setSavedSerialNumbers] = useState<{
		[key: string]: string[];
	}>({});

	// Add state to track if all serial numbers are provided
	const [allSerialNumbersProvided, setAllSerialNumbersProvided] =
		useState(false);

	// Add callback function to update saved serial numbers from child component
	const handleSavedSerialNumbersUpdate = (newSavedSerialNumbers: {
		[key: string]: string[];
	}) => {
		setSavedSerialNumbers(newSavedSerialNumbers);
	};

	/////////////////////////////////////////// FETCH DATA ///////////////////////////////////////////
	useEffect(() => {
		if (selectedItemsData) {
			const nonSerialized = selectedItemsData.filter((item) => {
				return item.itemData.item.inventoryType === "NON_SERIALIZED";
			});
			if (nonSerialized.length > 0) {
				const nonSerializedObject = nonSerialized.reduce(
					(acc, item) => {
						if (item.partNumber !== null) {
							acc[item.partNumber] = "";
						}
						return acc;
					},
					{} as { [key: string]: string }
				);

				Object.keys(nonSerializedObject).forEach((key) => {
					setValue(`partNumber: ${key}` as any, "");
				});
			}
		}
	}, [selectedItemsData]);

	useEffect(() => {
		const storedItems = localStorage.getItem("selectedItems");
		if (storedItems) {
			setSelectedItemsData(JSON.parse(storedItems));
		}

		if (optionsArray && optionsArray.length > 0) {
			const shippingCarriers = optionsArray[0].shippingCarriers;
			if (shippingCarriers) {
				let shipmentMethods = shippingCarriers.map(
					(carrier: ShippingCarrier) => {
						// TODO: Remove option without name from shippingMethods
						let shippingMethods = carrier.shippingMethods.filter(
							(method) => {
								return method.name !== "";
							}
						);
						return {
							carrierName: carrier.name,
							shippingMethods: shippingMethods,
						};
					}
				);

				let carrierAccountNumbers = shippingCarriers.map(
					(carrier: ShippingCarrier) => {
						return {
							carrierName: carrier.name,
							accountNumbers:
								carrier.shippingCarrierAccountNumbers.map(
									(account) => {
										return {
											uuid: account.uuid,
											name: `${carrier.name} - ${account.accountNumber}`,
										};
									}
								),
						};
					}
				);

				setCarrierAccountNumber(carrierAccountNumbers);
				setShippingMethodOptions(shipmentMethods);
			}
		}
	}, [optionsArray]);

	////////////////////////////////////////////// STYLES ////////////////////////////////////////////
	const updateFormClasses = `flex w-full space-x-3.5 bg-white p-4 mb-5 rounded-xl`;
	const updateFormIcon = `p-1.5 text-2xl mr-22px text-navy-270`;
	const buttonContainerClasses = `flex justify-end w-full max-lg:flex-col mt-12 max-md:justify-between max-md:items-end`;
	const guardrailBackgroundClasses = `fixed top-0 left-0 h-screen w-screen bg-black opacity-30 z-50`;
	const loadingScreenBackgroundClasses = `fixed top-0 left-0 h-screen w-screen z-50`;
	const fulfillAndShipButtonBg = `bg-[#3F5DCA]`;
	/////////////////////////////////////////// FORM SETUP ///////////////////////////////////////////

	const formMethods = useForm<ShipmentData>({
		mode: "all",
		defaultValues: {
			state: state,
			addressee: addressee,
			attention: attention,
			line1: line1,
			line2: line2,
			city: city,
			zip: zip,
			weight: "",
			signatureType: "Not Required",
			country: {
				name: "United States",
				uuid: "0bc27f7a-f17f-2312-f942-8c453bc815c3",
			},
			shipmentMethod: {
				name: "Ground",
				uuid: "410e9aa4-cf9d-7fb9-7c14-66bf5cf2debe",
			},
			name: `Shipment ${shipmentNumber}`,
		},
		reValidateMode: "onSubmit",
	});

	const {
		handleSubmit,
		control,
		watch,
		getValues,
		setError,
		clearErrors,
		setValue,
		formState: { errors },
	} = formMethods;
	const watchedFields = watch(["carrier", "shipmentMethod"]);
	const formValues = getValues();

	const getValueFromPath = (
		data: { [key: string]: any },
		valueKey: string
	): any => {
		return valueKey
			.split(".")
			.reduce(
				(accum: any, currentValue: any) =>
					accum ? accum[currentValue] : undefined,
				data
			);
	};

	// invoked when user deletes an item from the serial number modal, updates the form data and local storage
	const updateItemQuantity = (partNumber: string) => {
		const storedItems = localStorage.getItem("selectedItems");
		if (storedItems) {
			let items = JSON.parse(storedItems);

			let item = items.find(
				(item: ShipmentOrderSelectedItemTypes) =>
					item.partNumber === partNumber
			);

			if (item && item.quantity > 0) {
				item.quantity = (parseInt(item.quantity) - 1).toString();

				localStorage.setItem("selectedItems", JSON.stringify(items));
				setSelectedItemsData(items);
			}
		}
	};

	const areAllSerialNumbersProvided = () => {
		if (!selectedItemsData || selectedItemsData.length === 0) return false;

		// Check each item that requires serial numbers
		for (const item of selectedItemsData) {
			// Skip non-serialized items
			if (item.itemData?.item?.inventoryType === "NON_SERIALIZED") {
				continue;
			}

			const itemUuid = item.uuid;
			const requiredQuantity = parseInt(item.quantity as string);

			// For items with quantity > 1, check if serial numbers are saved
			if (requiredQuantity > 1) {
				const savedSerials = savedSerialNumbers[itemUuid] || [];
				if (savedSerials.length !== requiredQuantity) {
					return false;
				}
				// Check if all serial numbers are valid (not empty)
				if (
					savedSerials.some(
						(serial) => !serial || serial.trim() === ""
					)
				) {
					return false;
				}
			} else if (requiredQuantity === 1) {
				// For single quantity items, check both saved and form value
				const savedSerials = savedSerialNumbers[itemUuid] || [];
				const serialValue = getValues(`partNumber: ${itemUuid}` as any);

				// Check if either saved serial or form value exists
				const hasSavedSerial =
					savedSerials.length > 0 &&
					savedSerials[0] &&
					savedSerials[0].trim() !== "";
				const hasFormValue = serialValue && serialValue.trim() !== "";

				if (!hasSavedSerial && !hasFormValue) {
					return false;
				}
			}
		}

		return true;
	};

	useEffect(() => {
		const hasAllSerials = areAllSerialNumbersProvided();
		setAllSerialNumbersProvided(hasAllSerials);
	}, [savedSerialNumbers, selectedItemsData, formValues]);

	/////////////////////////////////////////// SUBMIT DATA ///////////////////////////////////////////
	const onFormSubmit = async (
		data: { [key: string]: any },
		formAction: string
	) => {
		setIsGuardrailOpen(false);

		// Check form for errors, missing fields, etc.
		validateFormOnSubmit(
			data,
			setError,
			clearErrors,
			dummyUpdateShipmentFields as ShipmentFieldsConfig,
			getValues
		);

		// format data for payload
		const newData = formatShipmentData(
			data,
			internalId as string,
			selectedItemsData,
			uuid,
			setError,
			clearErrors
		);

		const actualErrors = Object.keys(errors).filter(
			(key) => key && key.trim() !== ""
		);
		let hasErrors = actualErrors.length > 0;

		if (hasErrors) {
			// Reset loading states if there are errors
			setIsFulfillAndShipLoading(false);
			setIsSavingShipmentLoading(false);
			return; // Stop form submission if there are errors
		}

		try {
			if (formAction === "fulfillAndShip" && !hasErrors) {
				setIsFulfillAndShipLoading(true);
				const response = await saveShipment(newData);

				if (response?.status === 201) {
					const shipmentResponse = await fulfillShipment(
						response?.itemFulfillmentUuid
					);
					addToaster({
						isOpen: true,
						message: "Order fulfilled and shipped successfully",
						status: "success",
					});

					// Call the function to download the PDF
					await handleShipmentDownload(shipmentResponse);
					navigate(shipmentsUrl);
				} else {
					addToaster({
						isOpen: true,
						message: "Error fulfilling shipment",
						status: "error",
					});
					setIsFulfillAndShipLoading(false);
				}
			} else if (formAction === "delete" && !hasErrors) {
				// Perform delete action
				navigate(shipmentsUrl);
			} else if (formAction === "save" && !hasErrors) {
				setIsSavingShipmentLoading(true);

				try {
					const response = await saveShipment(newData);
					if (response?.status === 201) {
						addToaster({
							isOpen: true,
							message: "Saved successfully",
							status: "success",
						});
						navigate(shipmentsUrl);
					} else {
						addToaster({
							isOpen: true,
							message: "Error saving shipment",
							status: "error",
						});
						setIsSavingShipmentLoading(false);
					}
				} catch (error) {
					console.error("Error saving shipment:", error);
					addToaster({
						isOpen: true,
						message: "Error saving shipment",
						status: "error",
					});
					setIsSavingShipmentLoading(false);
				}
			} else if (formAction === "saveAndCreateNew" && !hasErrors) {
				setIsFulfillAndShipLoading(true);

				try {
					const response = await saveShipment(newData);
					if (response?.status === 201) {
						addToaster({
							isOpen: true,
							message:
								"Saved successfully, creating new shipment",
							status: "success",
						});
						navigate(shipmentsItemsUrl);
					} else {
						addToaster({
							isOpen: true,
							message: "Error saving shipment",
							status: "error",
						});
						setIsFulfillAndShipLoading(false);
					}
				} catch (error) {
					console.error("Error saving shipment:", error);
					addToaster({
						isOpen: true,
						message: "Error saving shipment",
						status: "error",
					});
					setIsFulfillAndShipLoading(false);
				}
			}
		} catch (error) {
			console.error("Unexpected error in form submission:", error);
			setIsFulfillAndShipLoading(false);
			setIsSavingShipmentLoading(false);
			addToaster({
				isOpen: true,
				message: "An unexpected error occurred",
				status: "error",
			});
		}
	};
	const handleFulfillAndShip = () => {
		onFormSubmit(getValues(), "fulfillAndShip");
	};
	const openGuardrail = () => {
		setIsGuardrailOpen(true);
	};

	// renders the correct loading message/screen based on submit action (fulfill and ship vs. save)
	const renderLoadingScreen = (
		isLoading: boolean,
		loadingMessage: string
	) => {
		return isLoading ? (
			<>
				<div className={loadingScreenBackgroundClasses}>
					{""}
					<FulfilledShipLoadingScreen
						containerClasses="flex flex-col items-center justify-center bg-white h-full w-full"
						loadingMessage={loadingMessage}
					/>
				</div>
			</>
		) : null;
	};

	/////////////////////////////////////////// FORMAT CONTENT ////////////////////////////////////////

	// Memoized content for form fields at the top of the update shipment page
	const formContent = React.useMemo(() => {
		return dummyUpdateShipmentFields.DUMMYUPDATESHIPMENTFIELDS?.map(
			(fieldSection: { [key: string]: any }, index: number) => {
				const columnFields = fieldSection.fields;
				const sectionTitle = fieldSection.sectionTitle;
				const sectionClasses =
					sectionClassesMap[fieldSection.sectionClasses];

				return (
					optionsArray &&
					shipmentNumber && (
						<div
							key={`${index}-${""}`}
							className={`${sectionClasses}`}
						>
							{sectionTitle && (
								<div className="flex items-center w-full">
									<BaseText
										text={sectionTitle}
										size="text-xs"
										color="text-navy-350"
										tag="h3"
										fontFamily="font-omnes-medium"
										additionalClasses="uppercase text-left pt-1 text-nowrap"
									/>
								</div>
							)}
							{columnFields.map((field: ShipmentField) => {
								let options;
								let value = getValueFromPath(
									shipmentAddress,
									field.valueKey
								);

								if (field.valueKey === "state.name") {
									value =
										shipmentAddress?.address?.state?.name;
								}

								if (shippingMethodOptions) {
									options = getEditShipmentFormOptions(
										field,
										optionsArray,
										watchedFields,
										shippingMethodOptions,
										carrierAccountNumber
									);
								}
								if (field.valueKey === "itemQuantity") {
									const itemCount = selectedItemsData.reduce(
										(sum, item) => {
											const quantity = Number(
												item.quantity
											);
											return (
												sum +
												(Number.isFinite(quantity)
													? quantity
													: 0)
											);
										},
										0
									);

									value = itemCount;
								}

								return (
									<Fragment key={field.id}>
										{renderEditShipmentFormInput(
											field,
											value,
											options,
											selectedItemsData,
											shipmentAddress,
											watchedFields,
											control,
											setError,
											clearErrors
										)}
									</Fragment>
								);
							})}
						</div>
					)
				);
			}
		);
	}, [
		dummyUpdateShipmentFields,
		optionsArray,
		watchedFields,
		shipmentAddress,
		carrierAccountNumber,
		shipmentNumber,
		formValues,
	]);

	//////////////////////////////////////////////////////////////////////////////////////////////////

	return optionsArray && selectedItemsData ? (
		<>
			<AnimatePresence>
				<motion.div
					className="w-full"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{
						ease: "easeOut",
						duration: 0.3,
					}}
				>
					<FormProvider {...formMethods}>
						<form
							className={formClasses}
							onSubmit={handleSubmit((data) =>
								onFormSubmit(data, "submit")
							)}
						>
							<div className={updateFormClasses}>
								<div className={updateFormIcon}>
									{getFontAwesomeIcon("boxTaped", "solid")}
								</div>
								{formContent}
							</div>
							<SelectedShipmentItemsTable
								data={selectedItemsData}
								control={control}
								updateQuantity={updateItemQuantity}
								savedSerialNumbers={savedSerialNumbers}
								onSavedSerialNumbersUpdate={
									handleSavedSerialNumbersUpdate
								}
							/>
						</form>
					</FormProvider>
					<div className={buttonContainerClasses}>
						<BaseButton
							text="Save & Create New Shipment"
							hoverBackground={
								allSerialNumbersProvided
									? "hover:bg-primaryHoverLight"
									: ""
							}
							fontColor={
								allSerialNumbersProvided
									? "text-primary"
									: "text-navy-400"
							}
							borderColor={
								allSerialNumbersProvided
									? "border-1 border-primary"
									: "border-1 border-navy-200"
							}
							bgColor={
								!allSerialNumbersProvided ? "bg-navy-100" : ""
							}
							additionalClasses={`py-1 px-3 mr-2.5 max-md:mb-4 ${
								!allSerialNumbersProvided
									? "cursor-not-allowed border-1  border-navy-200 text-navy-400 bg-navy-200"
									: ""
							}`}
							shape="rounded-full"
							as="button"
							fontFamily="font-omnes-medium"
							buttonSize="h-7 w-56"
							icon={getFontAwesomeIcon("plus", "regular")}
							iconClasses={`pr-2 text-xs ${
								allSerialNumbersProvided
									? "text-primary"
									: "text-navy-400"
							}`}
							onClick={() => {
								onFormSubmit(getValues(), "saveAndCreateNew");
							}}
							fontSize="text-sm"
							isDisabled={!allSerialNumbersProvided}
						/>
						<BaseButton
							text="Save"
							hoverBackground={
								allSerialNumbersProvided
									? "hover:bg-primaryHoverLight"
									: ""
							}
							fontColor={
								allSerialNumbersProvided
									? "text-primary"
									: "text-navy-400"
							}
							borderColor={
								allSerialNumbersProvided
									? "border-1 border-primary"
									: "border-1 border-navy-200"
							}
							bgColor={
								!allSerialNumbersProvided ? "bg-navy-100" : ""
							}
							additionalClasses={`py-1 px-3 mr-2.5 max-md:mb-4 ${
								!allSerialNumbersProvided
									? "cursor-not-allowed border-1  border-navy-200 text-navy-400 bg-navy-200"
									: ""
							}`}
							shape="rounded-full"
							as="button"
							fontFamily="font-omnes-medium"
							buttonSize="h-7 w-28"
							onClick={() => {
								onFormSubmit(getValues(), "save");
							}}
							fontSize="text-sm"
							isDisabled={!allSerialNumbersProvided}
						/>
						<BaseButton
							text="Fulfill & Ship"
							hoverBackground={
								allSerialNumbersProvided
									? "hover:bg-[#18318B]"
									: ""
							}
							fontColor={
								allSerialNumbersProvided
									? "text-white"
									: "text-navy-400"
							}
							borderColor={
								allSerialNumbersProvided
									? "border-1 border-primary"
									: "border-1 border-navy-200"
							}
							bgColor={
								!allSerialNumbersProvided
									? "bg-navy-100"
									: fulfillAndShipButtonBg
							}
							additionalClasses={`py-1 px-3 mr-2.5 max-md:mb-4 ${
								!allSerialNumbersProvided
									? "cursor-not-allowed border-1  border-navy-200 text-navy-400 bg-navy-200"
									: ""
							}`}
							shape="rounded-full"
							as="button"
							fontFamily="font-omnes-medium"
							buttonSize="h-7 w-28"
							onClick={openGuardrail}
							fontSize="text-sm"
							isDisabled={!allSerialNumbersProvided}
						/>
					</div>
				</motion.div>
			</AnimatePresence>
			{isGuardrailOpen && (
				<>
					<div className={guardrailBackgroundClasses}>{""}</div>
					<SupplyFormGuardrail
						messages={{
							messageOne: `Fulfill and ship this shipment?`,
							messageTwo:
								"This will send the shipment to the carrier for label generation and printing.",
							isOpen: true,
						}}
						onSave={() => {
							handleFulfillAndShip();
						}}
						onCancel={() => setIsGuardrailOpen(false)}
						onDoNotSave={() => {
							// Don't Save - Navigate back to shipments page without saving
							setIsGuardrailOpen(false);
							navigate(shipmentsUrl);
						}}
						buttonText={{
							centerButtonText: "Cancel",
							rightButtonText: "Fulfill & Ship",
						}}
					/>
				</>
			)}
			{renderLoadingScreen(
				isFulfillAndShipLoading,
				"Fulfilling and shipping shipments..."
			)}

			{renderLoadingScreen(isSavingShipmentLoading, "Saving shipment...")}
		</>
	) : (
		<></>
	);
};

export default EditShipmentForm;
